<template>
  <div>
    <b-container fluid class="px-5">
      <div class="mt-4 d-flex justify-content-between">
        <back-arrow title="Modifier une chirurgie"/>
      </div>

      <b-card class="mt-2 mb-4 shadow-sm">

        <b-form @submit.stop.prevent="onSubmit">

          <b-form-group
            v-if="!hospitalizationId"
            id="animal-input"
            label="Animal *"
            label-for="animal-input"
            class="bold-label"
            :invalid-feedback="form.animal.invalidFeedback"
            :state="form.animal.state"
          >
            <AutoCompleteInput
              id="animal-select"
              placeholder="Sélectionner un animal"
              :options="animals"
              :initValue="form.animal"
              @didSelect="didSelectAnimal"
              @onReset="animalSelectInvalid = false"
              :invalid="animalSelectInvalid"
              invalid-message="Vous devez sélectionner un animal"
              required
            />
          </b-form-group>

          <b-form-group
            v-if="!hospitalizationId"
            id="hospitalization-input"
            label="Hospitalisation *"
            label-for="hospitalization-input"
            class="bold-label"
            :invalid-feedback="form.hospitalization.invalidFeedback"
            :state="form.hospitalization.state"
          >
            <b-form-select
              id="hospitalization-input"
              v-model="form.hospitalization.value"
              :options="hospitalizations"
              :state="form.hospitalization.state"
              :disabled="form.animal.value === null"
              required
            ></b-form-select>
          </b-form-group>

          <b-form-group
            label="Date *"
            label-for="startDateCal"
            class="bold-label"
            :description="'Format : ' + new Date().toLocaleDateString('fr-FR', {year: 'numeric', month: '2-digit', day: '2-digit'})"
            :invalid-feedback="form.startDate.invalidFeedback"
            :state="form.startDate.state"
          >
            <vc-date-picker
              ref="startDateCal"
              v-model="form.startDate.value"
              mode="date"
              locale="fr"
              color="orange"
              :popover="{ visibility: 'click' }"
              :first-day-of-week="2"
              :modelConfig="modelConfig"
              :attributes="datePickerAttributes"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="border px-2 py-1 rounded start-date-datepicker mt-2"
                  :class="{'is-invalid': form.startDate.state === false}"
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="Date de la chirurgie"
                  required
                />
              </template>
            </vc-date-picker>
          </b-form-group>

          <b-form-group
            id="surgeryStatus-input"
            label="Statut *"
            label-for="surgeryStatus-input"
            class="bold-label"
            :invalid-feedback="form.surgeryStatus.invalidFeedback"
            :state="form.surgeryStatus.state"
          >
            <b-form-select
              id="surgeryStatus-input"
              v-model="form.surgeryStatus.value"
              :options="surgeryStatuses"
              :state="form.surgeryStatus.state"
              required
            ></b-form-select>
          </b-form-group>

          <b-form-group
            id="name-input"
            label="Nom *"
            label-for="name-input"
            class="bold-label"
            :invalid-feedback="form.name.invalidFeedback"
            :state="form.name.state"
          >
            <b-form-input
              id="name-input"
              list="autocomplete-names-list"
              autocomplete="off"
              v-model="form.name.value"
              type="text"
              maxlength="250"
              :state="form.name.state"
              required
            ></b-form-input>

            <datalist id="autocomplete-names-list" v-if="autocompleteNames">
              <option v-for="name in autocompleteNames">{{ name }}</option>
            </datalist>

          </b-form-group>

          <b-form-group
            class="bold-label position-relative mt-4"
            label="Description"
            label-for="description-input"
            :state="form.description.state"
            :invalid-feedback="form.description.invalidFeedback"
          >

            <b-link v-if="nameIsInSurgeriesTemplates" @click="useSurgeryTemplate" class="use-template-link">
              Utiliser le modèle de compte-rendu pour remplir la description
            </b-link>

            <b-form-textarea
              id="description-input"
              class="mt-1"
              v-model="form.description.value"
              :state="form.description.state"
            ></b-form-textarea>

          </b-form-group>

          <div class="d-flex justify-content-end">
            <b-button type="submit" variant="secondary" :disabled="sendButtonDisabled">Valider</b-button>
          </div>
        </b-form>

      </b-card>
    </b-container>
  </div>
</template>

<script>

import api from '../../services/api';
import BackArrow from "../../components/BackArrow";
import AutoCompleteInput from "../../components/AutoCompleteInput";

export default {

  name: 'SurgeryEdit',

  components: {
    BackArrow,
    AutoCompleteInput
  },

  data() {
    return {

      hospitalizationId: this.$route.params.hospitalizationId, // Si l'utilisateur arrive depuis la partie "hospitalisation"
      surgeryId: this.$route.params.surgeryId,
      surgery: null,
      animalSelectInvalid: false,
      sendButtonDisabled: false,

      animals: [],
      hospitalizations: [],
      surgeryStatuses: [],

      surgeryTemplates: [],
      namesSurgeriesWithoutTemplates: [],

      modelConfig: {
        type: 'number',
        timeAdjust: '10:00:00'
      },

      datePickerAttributes: [
        {
          key: 'today',
          highlight: {
            color : "orange",
            fillMode: 'outline',
          },
          dates: new Date(),
        },
      ],

      form: {
        animal: {
          state: null,
          invalidFeedback: null,
          value: null
        },
        hospitalization: {
          state: null,
          invalidFeedback: null,
          value: null
        },
        startDate: {
          state: null,
          invalidFeedback: null,
          value: null
        },
        surgeryStatus: {
          state: null,
          invalidFeedback: null,
          value: null
        },
        name: {
          state: null,
          invalidFeedback: null,
          value: null
        },
        description: {
          state: null,
          invalidFeedback: null,
          value: null
        }
      }
    }
  },

  mounted () {
    this.fetchData()
  },

  computed: {

    autocompleteNames() {
      let names = [
        ...this.namesSurgeriesWithoutTemplates,
        ...this.surgeryTemplates.map(template => template.name)
      ]
      return names.sort()
    },

    nameIsInSurgeriesTemplates() {
      if (!this.form.name.value || this.form.name.value === "") {
        return false
      }
      return this.surgeryTemplateForName.length > 0
    },

    surgeryTemplateForName() {
      return this.surgeryTemplates.filter(template => template.name === this.form.name.value.trim())
    }

  },

  methods: {

    fetchData() {
      this.setAutocompleteNames()
      this.fetchDataStarter()
        .then(this.fetchSurgery)
    },

    async fetchDataStarter() {
      this.fetchAnimals()
      this.fetchSurgeryStatuses()
    },

    setAutocompleteNames() {
      api
        .get(`/surgery/autocompleteSourceForNames`)
        .then(response => {
          this.surgeryTemplates = response.data.surgeryTemplates
          this.namesSurgeriesWithoutTemplates = response.data.namesSurgeriesWithoutTemplates
        })
    },

    fetchAnimals() {
      api
        .get(`/animal/getHospitalizedAnimals`)
        .then(response => {
          this.animals = [{ value: null, text: 'Sélectionner un animal', disabled: true }]
          response.data.map(animal => {
            this.animals.push({value: animal.id, text: `${animal.name} (${animal.user.name})`})
          })
        })
    },

    fetchSurgeryStatuses() {
      api
        .get(`/surgeryStatus`)
        .then(response => {
          this.surgeryStatuses = response.data.map(surgeryStatus => ({value: surgeryStatus.id, text: surgeryStatus.name}))
          this.form.surgeryStatus.value = this.surgeryStatuses[0].value
        })
    },

    fetchSurgery() {
      api
        .get(`/surgery/${this.surgeryId}`)
        .then(response => {
          this.surgery = response.data
          this.fetchHospitalizations(this.surgery.hospitalization.animal.id)
        })
    },

    fetchHospitalizations(animalId) {
      api
        .get(`/hospitalization?animalId=${animalId}`)
        .then(response => {
          this.hospitalizations = [{ value: null, text: 'Sélectionner une hospitalisation', disabled: true }]
          response.data.map(hospitalization => {
            this.hospitalizations.push({value: hospitalization.id, text: `Hospitalisation du ${new Date(hospitalization.startDate).toLocaleDateString('fr-FR')} (#${hospitalization.id})`})
          })
          this.setInitialValues()
        })
    },

    setInitialValues() {
      this.form.animal.value = this.surgery.hospitalization.animal.id
      this.form.hospitalization.value = this.surgery.hospitalization.id
      this.form.startDate.value = this.surgery.startDate
      this.form.surgeryStatus.value = this.surgery.surgeryStatus.id
      this.form.name.value = this.surgery.name
      this.form.description.value = this.surgery.description
    },

    useSurgeryTemplate() {
      this.form.description.value = this.surgeryTemplateForName[0].description
    },

    didSelectAnimal(value){
      this.form.animal.value = value
      if(value != null) this.fetchHospitalizations(value)
    },

    onSubmit() {
      if(this.form.animal.value === null && this.hospitalizationId === null){
        this.animalSelectInvalid = true
        return
      }
      this.sendButtonDisabled = true

      let data = new FormData()
      data.append('hospitalizationId', this.form.hospitalization.value)
      data.append('startDate', this.form.startDate.value)
      data.append('surgeryStatusId', this.form.surgeryStatus.value)
      data.append('name', this.form.name.value)
      if (this.form.description.value && this.form.description.value.trim() !== "") {
        data.append('description', this.form.description.value)
      }

      api({
        method: 'POST',
        url: `/surgery/${this.surgery.id}`,
        data: data
      })
        .then((response) => {
          if (this.hospitalizationId) {
            this.goToSurgeryForHospitalization()
          } else {
            this.goToSurgeryShow()
          }
        })
        .catch((error) => {
          this.sendButtonDisabled = false
          if (error.response.status === 418) {
            const invalidField = error.response.data.invalidField
            this.form[invalidField]["state"] = false
            this.form[invalidField]["invalidFeedback"] = error.response.data.message
          }
        });
    },

    successToast(surgeryName) {
      this.$bvToast.toast(`Chirurgie \"${surgeryName}\" modifiée`, {
        autoHideDelay: 2000,
        toaster: 'b-toaster-bottom-right',
        noCloseButton: true
      })
    },

    goToSurgeryShow() {
      this.$router.push({
        name: 'SurgeryShow',
        params: {
          surgeryId: this.surgery.id
        }
      }, () => {
        this.$nextTick(() => {
          this.successToast(this.form.name.value)
        })
      })
    },

    goToSurgeryForHospitalization() {
      this.$router.push({
        name: 'SurgeryForHospitalization',
        params: {
          hospitalizationId: this.hospitalizationId
        }
      }, () => {
        this.$nextTick(() => {
          this.successToast(this.form.name.value)
        })
      })
    }
  },

  watch: {
    'surgery.hospitalization.startDate': function (newValue, oldValue) {
       this.minDate = new Date(newValue).toString()
    }
  },
}
</script>

<style scoped>

.start-date-datepicker {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--main-text-color);
  height: calc(1.5em + 0.75rem + 2px);
  border: 1px solid var(--neutral-300) !important;
  border-radius: 0.25rem;
  width: 100%;
}
.start-date-datepicker.is-invalid {
  border: solid var(--main-red) 1px !important;
  padding-right: calc(0.75em + 2.3125rem) !important;
  background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23767676' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") right 0.75rem center/8px 10px no-repeat, var(--main-white) url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23F4464F' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23F4464F' stroke='none'/%3e%3c/svg%3e") center right 1.75rem/calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) no-repeat
}

@media (min-width: 768px) {
  .use-template-link {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/surgery/SurgeryEdit.vue